/**
 * Rock Paper Scissors Game Logic
 * Best of 5 rounds
 */

export const CHOICES = {
  ROCK: 'rock',
  PAPER: 'paper',
  SCISSORS: 'scissors',
};

export const CHOICE_EMOJIS = {
  rock: '✊',
  paper: '✋',
  scissors: '✌️',
};

/**
 * Determine the winner of a single round
 * @param {string} choice1 - First player's choice
 * @param {string} choice2 - Second player's choice
 * @returns {string} 'player1', 'player2', or 'tie'
 */
export const determineRoundWinner = (choice1, choice2) => {
  if (choice1 === choice2) {
    return 'tie';
  }

  if (
    (choice1 === CHOICES.ROCK && choice2 === CHOICES.SCISSORS) ||
    (choice1 === CHOICES.PAPER && choice2 === CHOICES.ROCK) ||
    (choice1 === CHOICES.SCISSORS && choice2 === CHOICES.PAPER)
  ) {
    return 'player1';
  }

  return 'player2';
};

/**
 * Create initial game state for Rock Paper Scissors
 * @param {string} hostId - Host player ID
 * @param {string} guestId - Guest player ID
 * @returns {Object} Initial game state
 */
export const createInitialGameState = (hostId, guestId) => {
  return {
    currentRound: 1,
    maxRounds: 5,
    hostScore: 0,
    guestScore: 0,
    rounds: [],
    hostChoice: null,
    guestChoice: null,
    bothChosen: false,
    winner: null,
    isGameOver: false,
    startedAt: Date.now(),
  };
};

/**
 * Process a player's choice
 * @param {Object} gameState - Current game state
 * @param {string} playerId - Player ID making the choice
 * @param {string} choice - Player's choice (rock/paper/scissors)
 * @param {string} hostId - Host player ID
 * @returns {Object} Updated game state
 */
export const processChoice = (gameState, playerId, choice, hostId) => {
  const isHost = playerId === hostId;
  const newState = {
    ...gameState,
    rounds: gameState.rounds || [] // Ensure rounds array exists
  };

  if (isHost) {
    newState.hostChoice = choice;
  } else {
    newState.guestChoice = choice;
  }

  // Check if both players have chosen
  if (newState.hostChoice && newState.guestChoice) {
    newState.bothChosen = true;

    // Determine round winner
    const roundWinner = determineRoundWinner(newState.hostChoice, newState.guestChoice);

    // Update scores
    if (roundWinner === 'player1') {
      newState.hostScore = (newState.hostScore || 0) + 1;
    } else if (roundWinner === 'player2') {
      newState.guestScore = (newState.guestScore || 0) + 1;
    }

    // Save round result
    newState.rounds.push({
      round: newState.currentRound,
      hostChoice: newState.hostChoice,
      guestChoice: newState.guestChoice,
      winner: roundWinner,
      timestamp: Date.now(),
    });

    // Check if game is over (best of 5)
    const scoreToWin = Math.ceil(newState.maxRounds / 2);
    if (newState.hostScore >= scoreToWin) {
      newState.isGameOver = true;
      newState.winner = 'host';
    } else if (newState.guestScore >= scoreToWin) {
      newState.isGameOver = true;
      newState.winner = 'guest';
    }
  }

  return newState;
};

/**
 * Start next round
 * @param {Object} gameState - Current game state
 * @returns {Object} Updated game state
 */
export const startNextRound = (gameState) => {
  return {
    ...gameState,
    currentRound: gameState.currentRound + 1,
    hostChoice: null,
    guestChoice: null,
    bothChosen: false,
  };
};

/**
 * Get game status
 * @param {Object} gameState - Current game state
 * @returns {Object} Game status
 */
export const getGameStatus = (gameState) => {
  return {
    isOver: gameState.isGameOver,
    winner: gameState.winner,
    hostScore: gameState.hostScore,
    guestScore: gameState.guestScore,
    currentRound: gameState.currentRound,
  };
};

/**
 * Validate game state
 * @param {Object} gameState - Game state to validate
 * @returns {boolean} True if valid
 */
export const isValidGameState = (gameState) => {
  if (!gameState || typeof gameState !== 'object') {
    return false;
  }

  if (typeof gameState.currentRound !== 'number' || gameState.currentRound < 1) {
    return false;
  }

  if (typeof gameState.hostScore !== 'number' || typeof gameState.guestScore !== 'number') {
    return false;
  }

  return true;
};
